import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce'

interface Option {
  value: string
  label: string
}

type InputProps = {
  loadOptions?: (inputValue: string) => Promise<Option[]>
  keySelect?: string
  name?: string
  value?: Option | null
  onChangeValue?: (e: any) => void
  disabled?: boolean
  textPlaceholder?: string
  isClearable?: boolean
}

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    minWidth: '200px',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
}

const SelectAsyncComponent: FC<InputProps> = ({
  keySelect = null,
  name = '',
  disabled,
  onChangeValue,
  loadOptions,
  value = null,
  textPlaceholder = 'Escribe...',
  isClearable = true,
}) => {
  const {
    control,
    formState: {errors},
  } = useFormContext()

  const [isLoading, setIsLoading] = useState(false)
  const [defaultOptionsData, setDefaultOptionsData] = useState<Option[]>([])

  const requestSeqRef = useRef(0)
  const lastInputRef = useRef('')

  const fetchOptionsByTerm = useCallback(
    async (term: string) => {
      if (!loadOptions) return

      const reqId = ++requestSeqRef.current
      try {
        setIsLoading(true)
        const options = await loadOptions(term)
        if (reqId !== requestSeqRef.current) return
        setDefaultOptionsData(options || [])
      } catch {
        if (reqId !== requestSeqRef.current) return
        setDefaultOptionsData([])
      } finally {
        if (reqId === requestSeqRef.current) setIsLoading(false)
      }
    },
    [loadOptions],
  )

  const fetchInitialOptions = useCallback(async () => {
    await fetchOptionsByTerm('')
  }, [fetchOptionsByTerm])

  useEffect(() => {
    fetchInitialOptions()
  }, [fetchInitialOptions])

  const debouncedLoadOptions = useCallback(
    debounce((inputValue: string, callback: (options: Option[]) => void) => {
      if (!loadOptions) {
        callback([])
        return
      }

      lastInputRef.current = inputValue

      const reqId = ++requestSeqRef.current
      setIsLoading(true)

      loadOptions(inputValue)
        .then((options: Option[]) => {
          if (reqId !== requestSeqRef.current) return
          const safeOptions = options || []
          setDefaultOptionsData(safeOptions)
          callback(safeOptions)
        })
        .catch(() => {
          if (reqId !== requestSeqRef.current) return
          callback([])
        })
        .finally(() => {
          if (reqId === requestSeqRef.current) setIsLoading(false)
        })
    }, 600),
    [loadOptions],
  )

  return (
    <div className='sdx_form_input '>
      <Controller
        control={control}
        name={name}
        render={() => (
          <AsyncSelect
            key={keySelect ?? name}
            cacheOptions={false}
            defaultOptions={defaultOptionsData}
            loadOptions={debouncedLoadOptions}
            classNamePrefix='react-select'
            noOptionsMessage={() => 'No hay resultados'}
            menuPortalTarget={document.body}
            placeholder={textPlaceholder}
            styles={{...customStyles}}
            value={value}
            onMenuOpen={() => {
              const term = (lastInputRef.current || '').trim()
              if (term) {
                setDefaultOptionsData([]) // evita mostrar data inicial mientras carga búsqueda
                fetchOptionsByTerm(term)
                return
              }
              fetchInitialOptions()
            }}
            onChange={(val: any) => {
              if (onChangeValue) onChangeValue(val)

              if (!val) {
                lastInputRef.current = ''
                fetchInitialOptions()
              }
            }}
            isDisabled={disabled}
            isLoading={isLoading}
            isClearable={isClearable}
          />
        )}
      />
      {errors[name] && <p>{errors[name]?.message?.toString()}</p>}
    </div>
  )
}

export default SelectAsyncComponent
